import type { ComponentChildren } from "preact";
import { DownloadIcon } from "../../components/icons";
import { formatMoney } from "../../lib/format";
import { DateOnly } from "../../lib/DateOnly";
import type { InvoiceItem } from "../../lib/gboModels";
import { idleDownload, type DownloadEntry } from "./useInvoiceDownloads";
import type { getInvoiceViewModel } from "./invoiceViewModel";

export function InvoiceContent({
  page,
  children,
}: {
  page: {
    viewModel: ReturnType<typeof getInvoiceViewModel>;
    invoiceDownload: DownloadEntry;
    receiptDownloads: Record<string, DownloadEntry>;
    downloadInvoice: () => Promise<void>;
    downloadReceipt: (payment: InvoiceItem) => Promise<void>;
  };
  children?: ComponentChildren;
}) {
  const { downloadInvoice, downloadReceipt, invoiceDownload, receiptDownloads, viewModel } = page;

  return (
    <section class="invoice-page" aria-label="Invoice details">
      <div class="checkout-layout">
        <div class="checkout-main">
          <section class="invoice-document">
            <header class="document-header">
              <h2>Invoice {viewModel.document.code}</h2>
              <button
                class="secondary-action icon-action document-download"
                type="button"
                onClick={downloadInvoice}
                disabled={invoiceDownload.status === "downloading"}
                aria-busy={invoiceDownload.status === "downloading"}
              >
                <DownloadIcon class="button-icon" />
                <span>Download</span>
              </button>
              {invoiceDownload.status === "error" && (
                <p class="payment-error" role="alert">
                  {invoiceDownload.message}
                </p>
              )}
            </header>

            <dl class="document-facts">
              <div>
                <dt>Bill to</dt>
                <dd>{viewModel.document.billToName}</dd>
                <dd class="muted">{viewModel.document.billToAddress}</dd>
              </div>
              {viewModel.document.serviceLocation && (
                <div>
                  <dt>Service address</dt>
                  {viewModel.document.serviceLocationName && (
                    <dd>{viewModel.document.serviceLocationName}</dd>
                  )}
                  <dd class="muted">{viewModel.document.serviceLocation}</dd>
                </div>
              )}
              <div class="date-facts">
                <div>
                  <dt>Issued</dt>
                  <dd>{DateOnly.format(viewModel.document.issuedOn)}</dd>
                </div>
                <div>
                  <dt>Due</dt>
                  <dd>{DateOnly.format(viewModel.document.dueBy)}</dd>
                </div>
              </div>
            </dl>
          </section>

          {viewModel.document.voided ? (
            <div class="paid-state" role="status">
              <strong>Invoice void</strong>
              <span>This invoice has been voided. No payment is due.</span>
            </div>
          ) : (
            viewModel.noPaymentDue && (
              <div class="paid-state" role="status">
                <strong>No payment due</strong>
                <span>
                  This invoice is paid in full. You can still download the invoice PDF for your
                  records.
                </span>
              </div>
            )
          )}

          <section class="invoice-statement">
            <div class="item-list">
              {viewModel.statement.items.map((item) => {
                const display = getInvoiceItemDisplay(item);
                return (
                  <div class="line-item" key={item.id}>
                    <div>
                      <strong>{display.title}</strong>
                      {display.subtitle && <span>{display.subtitle}</span>}
                      {item.quantity != null && item.unitPrice != null && (
                        <span>{formatInvoiceItemDetail(item)}</span>
                      )}
                      {(item.type === "Payment" || item.type === "AccountCredit") && (
                        <span>{DateOnly.format(item.created.slice(0, 10))}</span>
                      )}
                      {item.reversal && (
                        <span class="item-adjustment">
                          {item.reversal.description} ·{" "}
                          {DateOnly.format(item.reversal.created.slice(0, 10))}
                        </span>
                      )}
                      {item.refunds.map((refund) => (
                        <span class="item-adjustment" key={refund.id}>
                          {formatRefundLabel(refund.description)} (-{formatMoney(refund.amount)}) ·{" "}
                          {DateOnly.format(refund.occurredAtUtc.slice(0, 10))}
                        </span>
                      ))}
                    </div>
                    <div class="item-amount">
                      <strong class={item.reversal ? "reversed-amount" : undefined}>
                        {formatMoney(item.totalAmount)}
                      </strong>
                      {item.type === "Payment" && (
                        <ReceiptDownloadButton
                          download={receiptDownloads[item.id] ?? idleDownload}
                          onDownload={() => downloadReceipt(item)}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <ReceiptDownloadErrors
              downloads={receiptDownloads}
              payments={viewModel.statement.items}
            />
            <div class="statement-footer">
              <section class="totals" aria-label="Invoice totals">
                <dl>
                  {viewModel.statement.totals.map((total) => (
                    <div class={total.emphasis ? "total-due" : undefined} key={total.label}>
                      <dt>{total.label}</dt>
                      <dd>{formatMoney(total.amount)}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            </div>
          </section>
        </div>
        <aside class="checkout-rail" aria-label="Payment summary">
          <div class="payment-due-card">
            <span>{viewModel.paymentRail.balanceLabel}</span>
            {viewModel.paymentRail.balanceValueKind === "paid" ? (
              <strong>Paid in full</strong>
            ) : (
              <strong>{formatMoney(viewModel.paymentRail.balanceValue)}</strong>
            )}
          </div>
          {children}
        </aside>
      </div>
    </section>
  );
}

function ReceiptDownloadButton({
  download,
  onDownload,
}: {
  download: DownloadEntry;
  onDownload: () => void;
}) {
  return (
    <button
      class="link-action receipt-link"
      type="button"
      onClick={onDownload}
      disabled={download.status === "downloading"}
    >
      Download receipt
    </button>
  );
}

function ReceiptDownloadErrors({
  downloads,
  payments,
}: {
  downloads: Record<string, DownloadEntry>;
  payments: InvoiceItem[];
}) {
  for (const payment of payments) {
    const download = downloads[payment.id];
    if (download?.status === "error" && download.message)
      return (
        <p class="payment-error statement-error" role="alert">
          {download.message}
        </p>
      );
  }

  return null;
}

function formatInvoiceItemDetail(item: InvoiceItem) {
  const unitText = `${item.quantity} x ${formatMoney(item.unitPrice!)}`;
  return item.taxAmount != null && item.taxAmount > 0
    ? `${unitText} · ${formatMoney(item.taxAmount)} tax`
    : unitText;
}

function getInvoiceItemDisplay(item: InvoiceItem) {
  if (item.type !== "AccountCredit") return { title: item.description };

  const subtitle = item.description.replace(/^Account Credit\s*-\s*/i, "").trim();
  return {
    title: "Account Credit",
    subtitle: subtitle === "" ? undefined : subtitle,
  };
}

function formatRefundLabel(description: string) {
  const trimmed = description.trim();
  if (trimmed === "") return "Refund";
  return /^refund\b/i.test(trimmed) ? trimmed : `Refund - ${trimmed}`;
}
